import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Observable, of, interval, fromEvent, merge, timer } from 'rxjs';
import { catchError, map, switchMap, filter, take, debounceTime, startWith } from 'rxjs/operators';

/**
 * Version information from build process
 */
export interface AppVersion {
  version: number;
  buildTimestamp: string;
  commitHash: string;
  branch: string;
  buildDate: string;
}

/**
 * Enhanced service to detect application version changes with robust cache-busting
 *
 * FEATURES:
 * - ✅ Periodic version checks (every 10 minutes)
 * - ✅ Check on window focus/visibility change
 * - ✅ Check on route navigation
 * - ✅ Robust cache-busting with random hash
 * - ✅ User notification before reload (console + optional UI)
 * - ✅ Prevents race conditions
 * - ✅ Graceful error handling
 * - ✅ Console logging for debugging
 *
 * BEST PRACTICES IMPLEMENTED:
 * 1. Multiple check triggers for comprehensive coverage
 * 2. User-friendly notification system
 * 3. Prevents data loss by warning users
 * 4. Aggressive cache-busting strategies
 * 5. Proper initialization handling
 */
@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {

  private currentVersion: number | null = null;
  private isChecking = false;
  private isInitialized = false;
  private checkIntervalSubscription: any;
  private pendingReload = false;

  // Configuration
  private readonly CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
  private readonly RELOAD_DELAY_MS = 3000; // 3 seconds delay before reload

  constructor(
    private readonly http: HttpClient,
    private readonly ngZone: NgZone
  ) {
    this.initializeCurrentVersion();
    this.setupPeriodicChecks();
    this.setupVisibilityChangeChecks();
  }

  /**
   * Initialize by fetching the current version on first load
   * Waits for initialization before allowing version checks
   */
  private initializeCurrentVersion(): void {
    this.fetchVersionFromServer().subscribe({
      next: (version) => {
        if (version) {
          this.currentVersion = version.version;
          this.isInitialized = true;
          console.log('✅ [VersionCheck] Current version initialized:', {
            version: this.currentVersion,
            commit: version.commitHash,
            build: version.buildTimestamp
          });
        }
      },
      error: (error) => {
        console.warn('⚠️  [VersionCheck] Could not initialize version:', error.message);
        // Still mark as initialized to prevent blocking the app
        this.isInitialized = true;
      }
    });
  }

  /**
   * Setup periodic version checks every 10 minutes
   * Runs outside Angular zone for performance
   */
  private setupPeriodicChecks(): void {
    this.ngZone.runOutsideAngular(() => {
      this.checkIntervalSubscription = interval(this.CHECK_INTERVAL_MS)
        .pipe(
          startWith(0), // Check immediately after first interval
          switchMap(() => this.ngZone.run(() => this.checkVersion()))
        )
        .subscribe();
    });
  }

  /**
   * Setup version checks when user returns to tab (visibility change or focus)
   * Debounced to prevent multiple rapid checks
   */
  private setupVisibilityChangeChecks(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      // Combine both visibility change and window focus events
      merge(
        fromEvent(document, 'visibilitychange'),
        fromEvent(window, 'focus')
      )
        .pipe(
          debounceTime(1000), // Debounce to prevent rapid checks
          filter(() => !document.hidden), // Only check when page is visible
          switchMap(() => this.ngZone.run(() => this.checkVersion()))
        )
        .subscribe();
    });
  }

  /**
   * Check if a new version is available on the server
   * If a new version is detected, notify user and reload
   *
   * @returns Observable that completes after check (or reload)
   */
  checkVersion(): Observable<void> {
    // Prevent concurrent checks
    if (this.isChecking || this.pendingReload) {
      return of(void 0);
    }

    // Wait for initialization to complete
    if (!this.isInitialized) {
      console.log('⏳ [VersionCheck] Waiting for initialization...');
      return of(void 0);
    }

    // Skip check if no current version yet
    if (this.currentVersion === null) {
      console.warn('⚠️  [VersionCheck] No current version set, skipping check');
      return of(void 0);
    }

    this.isChecking = true;

    return this.fetchVersionFromServer().pipe(
      map((serverVersion) => {
        this.isChecking = false;

        if (!serverVersion) {
          console.warn('⚠️  [VersionCheck] No server version available');
          return;
        }

        // Version mismatch detected - notify and reload
        if (serverVersion.version !== this.currentVersion) {
          console.log('🔄 [VersionCheck] New version detected! Preparing reload...', {
            current: this.currentVersion,
            new: serverVersion.version,
            commit: serverVersion.commitHash,
            timeDiff: this.currentVersion !== null ? serverVersion.version - this.currentVersion : 'N/A'
          });

          this.pendingReload = true;
          this.notifyAndReload(serverVersion);
        } else {
          console.log('✓ [VersionCheck] Version up to date:', this.currentVersion);
        }
      }),
      catchError((error) => {
        this.isChecking = false;
        console.warn('⚠️  [VersionCheck] Version check failed:', error.message);
        return of(void 0);
      })
    );
  }

  /**
   * Notify user and schedule reload
   * Gives user a brief moment to see the notification and save work
   */
  private notifyAndReload(newVersion: AppVersion): void {
    // Display console notification with styling
    console.log(
      '%c🚀 NEW VERSION AVAILABLE! %c\n' +
      `Current: ${this.currentVersion}\n` +
      `New: ${newVersion.version}\n` +
      `Commit: ${newVersion.commitHash}\n` +
      `Build: ${newVersion.buildDate}\n` +
      `Reloading in ${this.RELOAD_DELAY_MS / 1000} seconds...`,
      'color: #4CAF50; font-size: 16px; font-weight: bold',
      'color: #666; font-size: 12px'
    );

    // Optional: Integrate with your notification system here
    // Example: this.notificationService.show('New version available! Updating...', 'info');

    // Schedule reload after delay to give user time to see notification and save work
    timer(this.RELOAD_DELAY_MS)
      .pipe(take(1))
      .subscribe(() => this.forceReload());
  }

  /**
   * Fetch version information from server
   * Uses AGGRESSIVE cache-busting with random hash + timestamp
   */
  private fetchVersionFromServer(): Observable<AppVersion | null> {
    // Triple cache-busting strategy:
    // 1. Timestamp (defeats time-based caching)
    // 2. Random hash (defeats fingerprint caching)
    // 3. Cache-control headers (explicit no-cache directive)
    const timestamp = Date.now();
    const randomHash = Math.random().toString(36).substring(2, 15);
    const url = `/version.json?v=${timestamp}&r=${randomHash}`;

    return this.http.get<AppVersion>(url, {
      headers: {
        'Cache-Control': 'no-store, no-cache, must-revalidate, proxy-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    }).pipe(
      catchError((error) => {
        console.warn('⚠️  [VersionCheck] Failed to fetch version:', error);
        return of(null);
      })
    );
  }

  /**
   * Force a hard reload of the application
   * Uses multiple strategies to bypass ALL caches
   */
  private forceReload(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      // Strategy 1: Clear session storage
      sessionStorage.clear();
      console.log('✓ [VersionCheck] Session storage cleared');
    } catch (e) {
      console.warn('⚠️  [VersionCheck] Could not clear session storage:', e);
    }

    try {
      // Strategy 2: Clear all caches via Cache API (for PWA/Service Workers)
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        });
        console.log('✓ [VersionCheck] Cache API cleared');
      }
    } catch (e) {
      console.warn('⚠️  [VersionCheck] Could not clear Cache API:', e);
    }

    // Strategy 3: Unregister service workers if any
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => registration.unregister());
      });
    }

    // Strategy 4: Add cache-busting parameter to URL
    const url = new URL(window.location.href);
    url.searchParams.set('_v', Date.now().toString());

    console.log('🔄 [VersionCheck] Performing hard reload...');

    // Strategy 5: Hard reload with cache bypass
    // Use replace to prevent back button issues
    window.location.replace(url.toString());
  }

  /**
   * Get the current application version
   */
  getCurrentVersion(): number | null {
    return this.currentVersion;
  }

  /**
   * Check if service is initialized
   */
  isServiceInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Manually trigger version check
   * Useful for testing or manual refresh buttons
   */
  manualCheck(): Observable<void> {
    console.log('🔍 [VersionCheck] Manual check triggered');
    return this.checkVersion();
  }

  /**
   * Cleanup subscriptions (called on service destroy)
   */
  cleanup(): void {
    if (this.checkIntervalSubscription) {
      this.checkIntervalSubscription.unsubscribe();
    }
  }
}
